/**
 * VehicleRegisterPage — `/fleet/register`. Live · POST /v1/vehicles.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createVehicle, type VehicleKind } from '../../lib/api/delivery';
import { PageHeader } from '../shared/StaffUI';

const KIND_OPTIONS: readonly VehicleKind[] = ['VAN', 'MOTORBIKE', 'BICYCLE', 'TRUCK'];

export function VehicleRegisterPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState<string>('');
  const [kind, setKind] = useState<VehicleKind>('VAN');
  const [make, setMake] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [capacityKg, setCapacityKg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createVehicle({
        registration,
        kind,
        make,
        model,
        capacityKg: Number.parseInt(capacityKg, 10),
      }),
    onSuccess: (v) => {
      void qc.invalidateQueries({ queryKey: ['fleet', 'vehicles'] });
      navigate(`/fleet/vehicles/${encodeURIComponent(v.id)}`);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (Number.isNaN(Number.parseInt(capacityKg, 10))) {
      setError('Capacity must be a number');
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader
        title="Register Vehicle"
        description="Add a new vehicle to the operator fleet registry."
        actions={
          <Link to="/fleet" className="px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white">
            Back to fleet
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="bg-white border border-border rounded-xl p-5 space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Registration plate</span>
          <input value={registration} onChange={(e) => setRegistration(e.target.value)} required maxLength={40} className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Vehicle kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as VehicleKind)} className="w-full px-3 py-2 text-sm border border-border rounded-lg">
            {KIND_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-muted mb-1">Make</span>
            <input value={make} onChange={(e) => setMake(e.target.value)} required maxLength={60} className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-muted mb-1">Model</span>
            <input value={model} onChange={(e) => setModel(e.target.value)} required maxLength={60} className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Capacity (kg)</span>
          <input
            value={capacityKg}
            onChange={(e) => setCapacityKg(e.target.value)}
            required
            inputMode="numeric"
            pattern="[0-9]+"
            className="w-full px-3 py-2 text-sm border border-border rounded-lg"
          />
        </label>
        {error ? <p className="text-xs text-red-700">{error}</p> : null}
        <div>
          <button type="submit" disabled={mutation.isPending} className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50">
            {mutation.isPending ? 'Registering…' : 'Register vehicle'}
          </button>
        </div>
      </form>
    </div>
  );
}
